/**
 * /portal/signup - create a new customer account.
 *
 * Body: { email, password, company_name, responsible_contact, business_capacity,
 *         accept_terms, accept_avv, avv_version_id, statement_language, ... }
 *
 * OCP-895 - the signup collects everything the contract needs, so a new
 * customer is not asked again at the first order: the company name, the
 * responsible contact person (AGB V4 4.4, same fields and wording as the
 * settings form) and the business-capacity declaration (Präambel B, same
 * statement as the settings form). The wording comes from the public
 * `GET /v1/portal/auth/signup/declarations`, in the page's language, which the
 * request carries back; the server stores its own constants for that language
 * and refuses a signup without them (400). Without a served declaration the
 * checkbox is disabled - no local fallback sentence (OCP-895). The VAT ID is not asked
 * here - it never gates anything (see BusinessCapacityGate.tsx).
 *
 * Backend creates `portal_customers` + `portal_users(role: owner)` rows,
 * sends a verification email, and returns 201 with the same shape as
 * /v1/portal/auth/me. Client then has a fully-authenticated session
 * (cookie is set in the response) - we redirect straight to /portal/
 * with the email-verification banner active until the customer clicks
 * the link from their inbox.
 *
 * Validation here is intentionally light - server is the source of
 * truth (zod schema in OCP-111). We do enforce a 12-char min on the
 * client just so the submit button doesn't fire for obvious cases.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { getCapturedLanding } from '../lib/landing-capture';
import { extractRedeemCodeFromTarget } from '../lib/redeem-pending';
import { OrbotoLockup } from '../components/OrbotoLockup';
import { currentLocale } from '../i18n';
import { LegalFooter } from '../components/LegalFooter';
import { FALLBACK_AVV_STATEMENT, fallbackTermsStatement, termsTextHref, type TermsKind } from '../components/TermsAcceptanceGate';
import {
  EMPTY_RESPONSIBLE_CONTACT_DRAFT,
  isResponsibleContactDraftValid,
  ResponsibleContactFields,
  type ResponsibleContactDraft,
} from '../components/ResponsibleContactGate';
import { BusinessCapacityConfirmCheckbox, servedBusinessCapacityStatement } from '../components/BusinessCapacityGate';

interface SignupResponse {
  user: {
    id: string;
    customer_id: string;
    email: string;
    email_verified: boolean;
    role: 'owner';
    totp_enabled: false;
    // OCP-515 - persisted server-side when a code was carried through signup.
    pending_redeem_code: string | null;
  };
}

const MIN_PW_LENGTH = 12;

/** OCP-895 - `GET /v1/portal/auth/signup/declarations`: the same constants the settings GETs serve. */
export const SIGNUP_DECLARATIONS_PATH = '/v1/portal/auth/signup/declarations';

interface SignupDeclarationsResponse {
  responsible_contact: { confirmation_text: string; confirmation_texts: { de: string; en: string } };
  business_capacity: {
    statement: string;
    statement_version: string;
    statement_language?: 'de' | 'en';
    statements?: Record<'de' | 'en', { statement: string; statement_version: string }>;
  };
}

/** OCP-856 - one document the signup accepts: the version in force and the wording of its own checkbox. */
interface SignupDocument {
  id: string;
  kind: TermsKind;
  version: string;
  /** Served by `/v1/portal/terms/current`; the acceptance row stores exactly this text. */
  statement: string | null;
  /** OCP-856 - the language `statement` is in (the page's), sent back with the signup. */
  statementLanguage: 'de' | 'en' | null;
}

/**
 * Validate that `from` is a same-site relative path. Prevents open-
 * redirect via `?from=https://evil.example/` etc.
 */
function safeRedirectTarget(raw: string | null): string {
  if (!raw) return '/';
  // Must start with single `/`, not `//` (protocol-relative attacks).
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  // Strip any control chars defensively.
  // eslint-disable-next-line no-control-regex -- intentional: reject control chars in the redirect target (open-redirect / header-injection defense)
  if (/[\x00-\x1f]/.test(raw)) return '/';
  return raw;
}

export function SignupPage() {
  const { t } = useTranslation('signup');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromQuery = safeRedirectTarget(searchParams.get('from'));
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  // OCP-895 - the responsible contact (AGB V4 4.4) and the business-capacity
  // declaration (Präambel B), each with the wording the API serves.
  const [contact, setContact] = useState<ResponsibleContactDraft>(EMPTY_RESPONSIBLE_CONTACT_DRAFT);
  const [capacityConfirmed, setCapacityConfirmed] = useState(false);
  const [contactConfirmation, setContactConfirmation] = useState<string | null>(null);
  const [capacityStatement, setCapacityStatement] = useState<string | null>(null);
  const contactLanguage = currentLocale();
  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await api<SignupDeclarationsResponse>(`${SIGNUP_DECLARATIONS_PATH}?lang=${encodeURIComponent(contactLanguage)}`, {
          signal: ac.signal,
        });
        setContactConfirmation(r.responsible_contact.confirmation_texts?.[contactLanguage] ?? r.responsible_contact.confirmation_text);
        // OCP-895 - only a sentence the API serves in the page's language; else the checkbox stays disabled
        setCapacityStatement(servedBusinessCapacityStatement(r.business_capacity, contactLanguage));
      } catch {
        // Fall back to the catalogue copy; the server stores its own constants either way.
      }
    })();
    return () => ac.abort();
  }, [contactLanguage]);
  // OCP-525 - the Terms acceptance. Unchecked = no signup (server refuses too).
  const [acceptTerms, setAcceptTerms] = useState(false);
  // OCP-856 - the AVV is a separate agreement with its OWN mandatory checkbox;
  // the request carries `accept_avv: true` only when that box is ticked.
  const [acceptAvv, setAcceptAvv] = useState(false);
  // OCP-847 / OCP-856 - the AGB and the AVV in force, each with its served
  // statement; the page links exactly the version shown and sends the AVV id.
  const [agb, setAgb] = useState<SignupDocument | null>(null);
  const [avv, setAvv] = useState<SignupDocument | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = async (kind: TermsKind, set: (d: SignupDocument) => void) => {
      try {
        const r = await api<{ version: { id: string; version: string }; statement?: string; statement_language?: 'de' | 'en' }>(
          `/v1/portal/terms/current?${kind === 'avv' ? 'kind=avv&' : ''}lang=${encodeURIComponent(currentLocale())}`,
        );
        if (!cancelled) set({ id: r.version.id, kind, version: r.version.version, statement: r.statement ?? null, statementLanguage: r.statement_language ?? null });
      } catch {
        // 404 = not published: the server refuses the signup (terms_not_published / avv_not_published), the form shows that on submit.
      }
    };
    void load('agb', setAgb);
    void load('avv', setAvv);
    return () => {
      cancelled = true;
    };
  }, []);
  // OCP-856 - every document shown needs its own tick; OCP-895 - and the
  // company, the responsible contact and the business-capacity declaration
  const allTicked =
    acceptTerms &&
    (!avv || acceptAvv) &&
    companyName.trim().length > 0 &&
    isResponsibleContactDraftValid(contact) &&
    capacityStatement !== null &&
    capacityConfirmed;
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || !allTicked) return;
    if (password.length < MIN_PW_LENGTH) {
      setError(t('error.passwordTooShort', { min: MIN_PW_LENGTH }));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // OCP-176 - attribution captured at first page-load (in
      // main.tsx via captureLandingOnce). Optional fields: when
      // sessionStorage is empty or the values fail backend
      // validation, the backend silently treats them as missing.
      const landing = getCapturedLanding();
      // OCP-515 - when the visitor arrived from /redeem?code=X (the chooser
      // threads it through as `?from=/redeem?code=...`), carry the code into
      // the signup body so it is persisted server-side. This is what survives a
      // cross-device email-verify; the server format-gates + drops a bad value
      // without failing the signup.
      const redeemCode = extractRedeemCodeFromTarget(fromQuery) ?? undefined;
      await api<SignupResponse>('/v1/portal/auth/signup', {
        method: 'POST',
        body: {
          email: email.trim().toLowerCase(),
          password,
          company_name: companyName.trim(),
          // OCP-895 - recorded by the same services as the settings forms
          responsible_contact: {
            name: contact.name.trim(),
            email: contact.email.trim(),
            phone: contact.phone.trim() || null,
            position: contact.position.trim() || null,
            confirmed: true,
            confirmation_language: contactLanguage,
          },
          business_capacity: { confirmed: true, statement_language: contactLanguage },
          accept_terms: true,
          ...(avv && acceptAvv ? { avv_version_id: avv.id, accept_avv: true } : {}),
          // OCP-856 - the language of the two statements shown; the rows store exactly those sentences
          statement_language: agb?.statementLanguage ?? avv?.statementLanguage ?? currentLocale(),
          signup_referrer: landing?.referrer,
          signup_landing_path: landing?.landing_path,
          signup_ref: landing?.ref ?? undefined,
          redeem_code: redeemCode,
        },
      });
      // Server set the session cookie on the response - pull the fresh
      // identity into the context so the shell renders immediately
      // without a hard reload.
      await refresh();
      // Honour `?from=` query for product-CTA landings (e.g.
      // /mail/signup -> /signup?from=/mail -> post-signup lands at /mail).
      // safeRedirectTarget validates same-site to prevent open-redirect.
      navigate(fromQuery, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'terms_not_published') {
        setError(t('error.termsNotPublished'));
      } else if (err instanceof ApiError && err.code === 'avv_not_published') {
        setError(t('error.avvNotPublished'));
      } else if (err instanceof ApiError && err.code === 'avv_acceptance_required') {
        // OCP-847 - an AVV was published while the page was open.
        setError(t('error.avvChanged'));
      } else if (err instanceof ApiError && err.code === 'responsible_contact_required') {
        setError(t('error.contactRequired'));
      } else if (err instanceof ApiError && err.code === 'invalid_contact') {
        setError(t('error.contactInvalid'));
      } else if (err instanceof ApiError && err.code === 'business_capacity_required') {
        setError(t('error.capacityRequired'));
      } else if (err instanceof ApiError && err.code === 'company_name_required') {
        setError(t('error.companyRequired'));
      } else if (err instanceof ApiError && err.code === 'email_in_use') {
        setError(t('error.emailInUse'));
      } else if (err instanceof ApiError && err.code === 'weak_password') {
        setError(t('error.weakPassword'));
      } else if (err instanceof ApiError && err.status === 429) {
        setError(t('error.tooManyAttempts'));
      } else {
        setError(t('error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-gray-800 px-4 text-gray-900 dark:text-gray-100">
      <div className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <OrbotoLockup className="h-7 w-auto" />
          <div className="text-center text-xs text-gray-500">{t('header')}</div>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('email')}</span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('password')}</span>
            <input
              type="password"
              name="password"
              autoComplete="new-password"
              required
              minLength={MIN_PW_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
            <span className="text-xs text-gray-500">
              {t('passwordHint', { min: MIN_PW_LENGTH })}
            </span>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('company')}</span>
            <input
              type="text"
              name="company_name"
              autoComplete="organization"
              required
              maxLength={200}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              data-testid="signup-company"
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
            <span className="text-xs text-gray-500">{t('companyHelp')}</span>
          </label>

          {/* OCP-895 - the business-capacity declaration (Präambel B) for the
              company above, ticked next to the statement the API serves. */}
          <div className="flex flex-col gap-1">
            <BusinessCapacityConfirmCheckbox
              statement={capacityStatement}
              checked={capacityConfirmed}
              onChange={setCapacityConfirmed}
            />
            <span className="text-xs text-gray-500">{t('capacityVatHint')}</span>
          </div>

          {/* OCP-895 - the responsible contact person (AGB V4 4.4): the same
              fields and API-served authorization wording as the settings form. */}
          <fieldset className="rounded border border-gray-200 dark:border-gray-800 p-3" data-testid="signup-responsible-contact">
            <legend className="px-1 text-sm font-medium text-gray-700 dark:text-gray-300">{t('contactHeading')}</legend>
            <p className="text-xs text-gray-500">{t('contactIntro')}</p>
            <ResponsibleContactFields draft={contact} onChange={setContact} confirmationText={contactConfirmation} />
          </fieldset>

          {/* OCP-525 - the Terms acceptance. The text is served from the
              registry (public /terms), the checkbox is required, and the
              server records the acceptance against the current version.
              OCP-856 - one mandatory checkbox per document, each with its
              served statement and a link to exactly the version shown. */}
          <div className="flex flex-col gap-1 text-sm">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                name="accept_terms"
                required
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                data-testid="signup-accept-terms"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-700"
              />
              <span className="text-gray-700 dark:text-gray-300">{agb?.statement ?? fallbackTermsStatement()}</span>
            </label>
            <Link to={agb ? termsTextHref(agb) : '/terms'} target="_blank" rel="noopener" className="ml-6 text-xs text-brand-500 underline" data-testid="signup-terms-link">
              {agb ? t('readTermsVersion', { version: agb.version }) : t('readTerms')}
            </Link>
          </div>
          {avv && (
            <div className="flex flex-col gap-1 text-sm">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  name="accept_avv"
                  required
                  checked={acceptAvv}
                  onChange={(e) => setAcceptAvv(e.target.checked)}
                  data-testid="signup-accept-avv"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-700"
                />
                <span className="text-gray-700 dark:text-gray-300">{avv.statement ?? t('terms-acceptance:fallbackAvvStatement', { defaultValue: FALLBACK_AVV_STATEMENT })}</span>
              </label>
              <span className="ml-6 text-xs text-gray-500">{t('avvSeparate')}</span>
              <Link to={termsTextHref(avv)} target="_blank" rel="noopener" className="ml-6 text-xs text-brand-500 underline" data-testid="signup-avv-link">
                {t('readAvvVersion', { version: avv.version })}
              </Link>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !allTicked}
            className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {submitting ? t('submitting') : t('submit')}
          </button>

          <Link to="/login" className="text-center text-sm text-gray-500 hover:text-brand-500">
            {t('haveAccount')}
          </Link>
        </form>
        {/* Imprint, privacy, terms and the DB-IP credit (OCP-848) on every page. */}
        <LegalFooter className="mt-6" />
      </div>
    </div>
  );
}
